import { ComponentProps, useMemo } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "../../lib/utils";

export function AccordionTrigger({
  className,
  children,
  chevronPosition = "trailing",
  chevronDirection = "up-to-down",
  shiftIcon = false,
  withoutHeader = false,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Trigger> & {
  chevronPosition?: "leading" | "trailing" | "none";
  chevronDirection?: "up-to-down" | "right-to-down";
  shiftIcon?: boolean;
  withoutHeader?: boolean;
  hideChevron?: boolean;
}) {
  const Icon = useMemo(() => {
    switch (chevronDirection) {
      case "up-to-down":
        return ChevronDownIcon;
      case "right-to-down":
        return ChevronRightIcon;
    }
  }, [chevronDirection]);

  const trigger = (
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      className={cn(
        "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
        chevronDirection === "right-to-down" && "[&[data-state=open]>svg]:rotate-90",
        className
      )}
      {...props}
    >
      {chevronPosition === "leading" && (
        <Icon
          className={cn(
            "text-muted-foreground pointer-events-none size-4 shrink-0 transition-transform duration-200",
            shiftIcon && "translate-y-0.5"
          )}
        />
      )}
      {children}
      {chevronPosition === "trailing" && (
        <Icon
          className={cn(
            "text-muted-foreground pointer-events-none size-4 shrink-0 transition-transform duration-200",
            shiftIcon && "translate-y-0.5"
          )}
        />
      )}
    </AccordionPrimitive.Trigger>
  );

  if (withoutHeader) {
    return trigger;
  }

  return <AccordionPrimitive.Header className="flex">{trigger}</AccordionPrimitive.Header>;
}
